/**
 * Benefit status utilities for consistent badge styling across the app.
 */

import React from 'react';
import { Badge } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';

const BENEFIT_STATUS_CREATED = 'Created';
const BENEFIT_STATUS_HELD = 'Held';
const BENEFIT_STATUS_AUTOHELD = 'Auto-Held';
const BENEFIT_STATUS_APPROVED = 'Approved';
const BENEFIT_STATUS_AUTOAPPROVED = 'Auto-Approved';
const BENEFIT_STATUS_REJECTED = 'Rejected';

/**
 * Status configuration mapping status codes to their display properties.
 */
const STATUS_CONFIG = {
  APPROVED: { variant: 'success', label: BENEFIT_STATUS_APPROVED },
  AUTOAPPROVED: { variant: 'success', label: BENEFIT_STATUS_APPROVED, adminLabel: BENEFIT_STATUS_AUTOAPPROVED },
  REJECTED: { variant: 'danger', label: BENEFIT_STATUS_REJECTED },
  HELD: { variant: 'warning', label: BENEFIT_STATUS_HELD, textDark: true },
  AUTOHELD: { variant: 'warning', label: BENEFIT_STATUS_HELD, adminLabel: BENEFIT_STATUS_AUTOHELD, textDark: true },
  CREATED: { variant: 'info', label: BENEFIT_STATUS_CREATED },
};

/**
 * Get the Bootstrap variant for a benefit status.
 * @param {string} status - The benefit status code
 * @returns {string} Bootstrap variant (success, danger, warning, info, secondary, primary)
 */
const getBenefitStatusVariant = (status) => {
  const normalizedStatus = status?.toUpperCase();
  return STATUS_CONFIG[normalizedStatus]?.variant || 'secondary';
};

/**
 * Get the display label for a benefit status.
 * @param {string} status - The benefit status code
 * @returns {string} Human-readable status label
 */
const getBenefitStatusLabel = (status) => {
  const normalizedStatus = status?.toUpperCase();
  return STATUS_CONFIG[normalizedStatus]?.label || status || 'Unknown';
};

/**
 * Get full status configuration including variant, label, and text color.
 * @param {string} status - The benefit status code
 * @returns {Object} { variant, label, textDark }
 */
const getBenefitStatusConfig = (status) => {
  const normalizedStatus = status?.toUpperCase();
  const config = STATUS_CONFIG[normalizedStatus];

  if (config) {
    return {
      variant: config.variant,
      label: config.label,
      adminLabel: config.adminLabel,
      textDark: config.textDark || false,
    };
  }

  return {
    variant: 'secondary',
    label: status || 'Unknown',
    adminLabel: status || 'Unknown',
    textDark: false,
  };
};

/**
 * Reusable badge component for displaying benefit status.
 * @param {Object} props - Component props
 * @param {string} props.status - The benefit status code
 * @returns {JSX.Element} Badge component with appropriate styling
 */
const BenefitStatusBadge = ({ status }) => {
  const { variant, label, adminLabel, textDark } = getBenefitStatusConfig(status);
  const { isAdmin } = useAuth();

  return <Badge 
    bg={variant}
    text={textDark ? 'dark' : undefined}
    style={{ padding: '0.5rem', fontSize: '0.875rem', fontWeight: 'normal'}}
    className="benefit-status-badge">{isAdmin && (adminLabel !== undefined)? adminLabel: label}
  </Badge>;
};

export default BenefitStatusBadge;